import Button from "components/base/Button";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import api from "helpers/api";
import toast from "react-hot-toast";
import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  terms_service: boolean;
};

const SignUpForm = ({ layout, selectedPlan }: { 
  layout: "simple" | "card" | "split";
  selectedPlan?: string;
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();
  const navigate = useNavigate();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [invitationInfo, setInvitationInfo] = useState<{
    invitedBy?: string;
    brokerage?: string;
    email?: string;
  }>({});

  const passwordValue = watch("password");

  // Check for invitation parameters in URL
  useEffect(() => {
    const invitedBy = searchParams.get("invited_by") || undefined;
    const brokerage = searchParams.get("brokerage") || undefined;
    const email = searchParams.get("email") || undefined;

    if (invitedBy || brokerage || email) {
      setInvitationInfo({ invitedBy, brokerage, email });
      
      // Pre-fill email if provided
      if (email) {
        setValue("email", email);
      }
    }
  }, [searchParams, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      // If user came through broker invitation, check if they already exist
      if (invitationInfo.invitedBy && invitationInfo.brokerage) {
        try {
          // First, try to link existing user to brokerage
          const linkResponse = await api.post("/broker/invitations/link-existing-user", {
            email: data.email,
            brokerageName: invitationInfo.brokerage
          });

          if (linkResponse.data.userExists) {
            // User exists - prompt them to log in instead
            toast("You already have an account. Please log in to join the brokerage.", {
              icon: 'ℹ️',
              style: {
                background: '#3b82f6',
                color: '#fff',
              },
            });
            // Store brokerage info for after login
            localStorage.setItem('pendingBrokerageLink', JSON.stringify({
              brokerage: invitationInfo.brokerage,
              invitedBy: invitationInfo.invitedBy
            }));
            navigate("/sign-in", { 
              state: { 
                email: data.email,
                message: `Please sign in to join ${invitationInfo.brokerage}` 
              } 
            });
            return;
          }
        } catch (linkError) {
          // Continue with normal registration if linking fails
          console.log("Link check failed, continuing with registration");
        }
      }

      // If user came through broker invitation, force Essential plan
      const planToUse = invitationInfo.invitedBy ? "Essential" : selectedPlan;
      
      const response = await api.post("/auth/registration", {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        password: data.password,
        confirm_password: data.confirm_password,
        selected_plan: planToUse,
        invited_by: invitationInfo.invitedBy,
        brokerage_name: invitationInfo.brokerage,
      });

      // Check both response formats (direct and wrapped in data)
      const responseData = response.data.data || response.data;
      
      if (responseData.requiresPayment && responseData.stripeSessionId) {
        // Redirect to Stripe checkout for Essential plan payment
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY as string);
        if (stripe) {
          const result = await stripe.redirectToCheckout({
            sessionId: responseData.stripeSessionId,
          });
          if (result.error) {
            toast.error(result.error.message || 'Payment redirect failed');
          }
        }
      } else {
        toast.success(response.data.message || responseData.message || "Registration successful!");
        reset();
        navigate("/dashboard");
      }
    } catch (error: any) {
      // Special handling for "Email already registered" error
      if (error.response?.data?.message === "Email already registered.") {
        toast("You already have an account. Please log in to continue.", {
          icon: 'ℹ️',
          style: {
            background: '#3b82f6',
            color: '#fff',
          },
        });
        // Store brokerage info for after login
        if (invitationInfo.brokerage) {
          localStorage.setItem('pendingBrokerageLink', JSON.stringify({
            brokerage: invitationInfo.brokerage,
            invitedBy: invitationInfo.invitedBy
          }));
        }
        navigate("/sign-in", { 
          state: { 
            email: data.email,
            message: invitationInfo.brokerage ? 
              `Please sign in to join ${invitationInfo.brokerage}` : 
              "Please sign in to continue"
          } 
        });
      } else {
        toast.error(error.response?.data?.message || "Registration failed");
      }
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      // Store signup context in localStorage for after OAuth callback
      localStorage.setItem('oauth_signup_context', JSON.stringify({
        signup: true,
        paid: false,
        plan: selectedPlan || 'basic'
      }));

      // Redirect to OAuth signup endpoint
      if(process.env.NODE_ENV === "development") {
        window.location.href = `http://localhost:8080/api/google/signup/auth?signup=true&paid=false&plan=${selectedPlan || 'basic'}`;
      } else {
        window.location.href = `${process.env.APP_URL}/api/google/signup/auth?signup=true&paid=false&plan=${selectedPlan || 'basic'}`;
      }
    } catch (error) {
      toast.error("Google sign-up failed");
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      {/* KairosIQ AI Branded Header */}
      <div className="text-center mb-4">
        <div className="d-flex align-items-center justify-content-center mb-3">
          <div className="bg-primary rounded-circle p-3 me-3" style={{ background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' }}>
            <i className="fas fa-robot text-white" style={{ fontSize: '24px' }}></i>
          </div>
          <div>
            <h3 className="text-body-highlight mb-1" style={{ color: '#1f2937', fontWeight: '700' }}>
              Join KairosIQ AI
            </h3>
            <p className="text-body-tertiary mb-0" style={{ fontSize: '14px', color: '#6b7280' }}>
              {invitationInfo.brokerage ? 
                `Join ${invitationInfo.brokerage} on the Essential plan` : 
                selectedPlan && `Start your ${selectedPlan} plan today`}
            </p>
          </div>
        </div>
        {invitationInfo.invitedBy ? (
          <div className="alert alert-info" style={{ 
            background: 'rgba(0, 212, 255, 0.1)', 
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            color: '#0c5460',  // Dark blue text
            padding: '12px 20px'
          }}>
            <i className="fas fa-info-circle me-2" style={{ color: '#0ea5e9' }}></i>
            <strong style={{ color: '#0c5460' }}>{invitationInfo.invitedBy}</strong> from <strong style={{ color: '#0c5460' }}>{invitationInfo.brokerage}</strong> has invited you!
            <br />
            <small style={{ color: '#155724', fontWeight: '500' }}>You'll be enrolled in the Essential plan ($50/month) upon registration.</small>
          </div>
        ) : (
          <p className="text-body-tertiary" style={{ color: '#4b5563' }}>
            Create your account and start converting leads in 5 minutes
          </p>
        )}
      </div>

      {/* Google OAuth Sign Up Button */}
      <div className="mb-4">
        <Button
          variant="outline-primary"
          onClick={handleGoogleSignUp}
          disabled={isGoogleLoading}
          className="w-100 d-flex align-items-center justify-content-center py-3"
          style={{
            border: '2px solid #e5e7eb',
            background: 'white',
            color: '#374151',
            fontWeight: '600',
            fontSize: '16px',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}
        >
          {isGoogleLoading ? (
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <svg className="me-2" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Continue with Google
        </Button>
      </div>

      {/* Divider */}
      <div className="d-flex align-items-center mb-4">
        <hr className="flex-grow-1" style={{ borderColor: '#e5e7eb' }} />
        <span className="px-3 text-body-tertiary" style={{ fontSize: '14px', color: '#9ca3af' }}>
          Or create account manually
        </span>
        <hr className="flex-grow-1" style={{ borderColor: '#e5e7eb' }} />
      </div>

      {/* Manual Registration Form */}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="first_name" style={{ color: '#374151', fontWeight: '500' }}>
                First Name
              </Form.Label>
              <Form.Control
                id="first_name"
                type="text"
                placeholder="John"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("first_name", { required: true, maxLength: 100 })}
              />
              {errors.first_name?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  First name is required
                </p>
              )}
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="last_name" style={{ color: '#374151', fontWeight: '500' }}>
                Last Name
              </Form.Label>
              <Form.Control
                id="last_name"
                type="text"
                placeholder="Doe"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("last_name", { required: true, maxLength: 100 })}
              />
              {errors.last_name?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  Last name is required
                </p>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email" style={{ color: '#374151', fontWeight: '500' }}>
            Email Address
          </Form.Label>
          <Form.Control
            id="email"
            type="email"
            placeholder="john@company.com"
            style={{
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              padding: '12px 16px',
              fontSize: '16px'
            }}
            {...register("email", { required: true, maxLength: 80 })}
          />
          {errors.email?.type === "required" && (
            <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
              Email address is required
            </p>
          )}
        </Form.Group>

        <Row className="g-3 mb-4">
          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="password" style={{ color: '#374151', fontWeight: '500' }}>
                Password
              </Form.Label>
              <Form.Control
                id="password"
                type="password"
                placeholder="••••••••"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("password", {
                  required: true,
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long.",
                  },
                  maxLength: {
                    value: 40,
                    message: "Password cannot exceed 40 characters.",
                  },
                })}
              />
              {errors.password?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  Password is required
                </p>
              )}
              {errors.password && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  {errors.password.message}
                </p>
              )}
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="confirmPassword" style={{ color: '#374151', fontWeight: '500' }}>
                Confirm Password
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("confirm_password", {
                  required: true,
                  validate: (value) =>
                    value === passwordValue || "Passwords do not match",
                })}
              />
              {errors.confirm_password && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  {errors.confirm_password.message}
                </p>
              )}
            </Form.Group>
          </Col>
        </Row>

        {/* Enhanced Terms Checkbox */}
        <Form.Check type="checkbox" className="mb-4">
          <Form.Check.Input
            type="checkbox"
            id="termsService"
            style={{ borderRadius: '4px' }}
            {...register("terms_service", {
              required: "You must accept the terms and privacy policy",
            })}
          />
          <Form.Check.Label
            htmlFor="termsService"
            style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}
          >
            I agree to KairosIQ's{" "}
            <Link 
              to="/terms-and-conditions" 
              target="_blank"
              style={{ color: '#00d4ff', textDecoration: 'none' }}
            >
              Terms of Service
            </Link>
            {" "}and{" "}
            <Link 
              to="/privacy-policy" 
              target="_blank"
              style={{ color: '#00d4ff', textDecoration: 'none' }}
            >
              Privacy Policy
            </Link>
            , including the use of AI to analyze email communications for real estate insights.
          </Form.Check.Label>
          {errors.terms_service && (
            <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
              {errors.terms_service.message}
            </p>
          )}
        </Form.Check>

        {/* Create Account Button */}
        <Button 
          variant="primary" 
          type="submit" 
          className="w-100 mb-3"
          style={{
            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
          }}
        >
          <i className="fas fa-rocket me-2"></i>
          Create Account & Start Free Trial
        </Button>

        {/* Sign In Link */}
        <div className="text-center">
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            Already have an account?{" "}
            <Link 
              to="/sign-in" 
              style={{ 
                color: '#00d4ff', 
                textDecoration: 'none', 
                fontWeight: '600' 
              }}
            >
              Sign in here
            </Link>
          </span>
        </div>
      </Form>

      {/* Security Notice */}
      <div 
        className="mt-4 p-3 text-center"
        style={{
          background: 'rgba(0, 212, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(0, 212, 255, 0.1)'
        }}
      >
        <i className="fas fa-shield-alt text-primary me-2"></i>
        <span style={{ fontSize: '12px', color: '#4b5563' }}>
          Your data is encrypted and secure. We never share your information.
        </span>
      </div>
    </>
  );
};

export default SignUpForm;
